import { TIMEFRAME_MINUTES, type Candle, type Timeframe } from './types'

/**
 * Générateur de bougies de DÉMONSTRATION.
 *
 * ⚠️ Ces données ne correspondent à **aucun marché réel**. Elles n'existent que
 * dans le navigateur, uniquement quand l'utilisateur active explicitement le
 * mode démonstration, et l'interface affiche alors un bandeau permanent.
 *
 * Décision assumée : **le backend ne fabrique jamais de prix.** Une route
 * serveur capable de renvoyer de faux prix finirait un jour par en servir sans
 * qu'on s'en aperçoive. Ici, le mensonge est cantonné à un fichier du frontend
 * qu'aucun calcul d'ordre réel n'utilise.
 *
 * Sert à vérifier le rendu du graphique — zoom, intervalles, affichage
 * mobile — tant que le broker n'est pas connecté.
 */

const MINUTES = TIMEFRAME_MINUTES

/** Générateur pseudo-aléatoire déterministe : le même graphique à chaque fois. */
function seeded(seed: number) {
  let value = seed
  return () => {
    value = (value * 1664525 + 1013904223) % 4294967296
    return value / 4294967296
  }
}

export function generateDemoCandles(
  symbol: string,
  timeframe: Timeframe,
  count = 200,
): Candle[] {
  // La graine dérive du symbole : chaque paire a sa propre allure, stable
  // d'un affichage à l'autre.
  const seed = [...symbol].reduce((acc, char) => acc + char.charCodeAt(0), 7)
  const random = seeded(seed)

  const isJpy = symbol.endsWith('JPY')
  const decimals = isJpy ? 3 : 5
  let price = isJpy ? 150 : 1.1
  const volatility = price * 0.0006

  const stepSeconds = MINUTES[timeframe] * 60
  const start = Math.floor(Date.now() / 1000 / stepSeconds) * stepSeconds - count * stepSeconds

  const candles: Candle[] = []
  for (let index = 0; index < count; index += 1) {
    const open = price
    const drift = (random() - 0.5) * volatility * 4
    const close = open + drift
    const high = Math.max(open, close) + random() * volatility
    const low = Math.min(open, close) - random() * volatility
    price = close

    candles.push({
      time: start + index * stepSeconds,
      open: open.toFixed(decimals),
      high: high.toFixed(decimals),
      low: low.toFixed(decimals),
      close: close.toFixed(decimals),
      volume: Math.round(500 + random() * 2000),
    })
  }
  return candles
}
